import Location from '@/models/location'
import ImageAssetOrUri from './imageAssetOrUri'
import { getPhotoUrl, type PhotoIdentifier } from '@/utils/photoUtils'
import { warn } from '@/utils/logger'

export default class PostLocation {
  _id?: string
  post_id?: string
  location: Location
  /** Set when API omits full location and only sends location_id (e.g. fetchPostsByLocationIdUnified). */
  location_id?: string
  body: string
  /** Explicit title for user-created places (stored by itinerary_slots path, distinct from body/notes). */
  name?: string
  address?: string // User-entered address for user-created places (free-form text)
  itinerary_day_index: number
  itinerary_day_order_index: number // The chronological order for the specified day
  image_urls: string[]
  gt_category?: string
  slotType?: string
  notes?: string
  budget?: number | string
  timeRange?: string
  is_user_created?: boolean

  imageAssetOrUris: ImageAssetOrUri[] = [] // Just here for adding new PostLocations.

  constructor(data: Record<string, unknown>) {
    this._id = data._id as string | undefined
    this.post_id = data.post_id as string | undefined
    this.location = new Location((data.location as Record<string, unknown>) ?? {})
    this.location_id = data.location_id as string | undefined
    this.body = (data.body as string) ?? ''
    this.name = data.name as string | undefined
    this.address = data.address as string | undefined
    this.itinerary_day_index = (data.itinerary_day_index as number) ?? -1
    this.itinerary_day_order_index = (data.itinerary_day_order_index as number) ?? -1
    this.image_urls = (data.image_urls as string[]) ?? []
    // Extract new fields from backend response
    this.gt_category = data.gt_category as string | undefined
    this.slotType = data.slotType as string | undefined
    this.notes = data.notes as string | undefined
    this.budget = data.budget as number | string | undefined
    this.timeRange = (data.timeRange as string) || (data.time as string) // Backend stores as 'time', we read as 'timeRange'
    // Handle both snake_case and camelCase from backend
    this.is_user_created = (data.is_user_created as boolean) ?? (data.isUserCreated as boolean) ?? false

    // Migrate old S3 bucket URLs to new bucket
    // Also handle photo identifier objects (backend now sends identifiers, not URLs)
    if (this.image_urls.length > 0) {
      this.image_urls = (this.image_urls as (string | PhotoIdentifier)[]).map((url) => {
        if (typeof url !== 'string') {
          // Handle new photo identifier format (backend sends identifiers only)
          if (url?.name || url?.photo_reference) {
            const photoUrl = getPhotoUrl(url as PhotoIdentifier, 400)
            if (photoUrl) {
              return photoUrl
            }
            // If conversion failed, log warning but continue
            warn('[PostLocation] Failed to convert photo identifier to URL:', {
              hasName: !!url.name,
              hasPhotoReference: !!url.photo_reference,
              photoKeys: Object.keys(url),
            })
            return '' // Return empty string instead of object
          }
          // Unknown format - log warning
          warn('[PostLocation] Image URL is not a string and not a photo identifier:', {
            urlType: typeof url,
            urlKeys: url ? Object.keys(url) : [],
            url,
          })
          return '' // Return empty string instead of object
        }
        return url
          .replace(
            'globethriversmedia112710-prod.s3.eu-west-1.amazonaws.com',
            'globethriversmedia87608-prodnew.s3.eu-west-1.amazonaws.com'
          )
          .replace(
            'globethriversmedia112710-prod.s3-accelerate.amazonaws.com',
            'globethriversmedia87608-prodnew.s3.eu-west-1.amazonaws.com'
          )
      }).filter((url): url is string => typeof url === 'string' && url.length > 0) // Filter out empty strings
    }

    this.imageAssetOrUris = this.image_urls.map(
      url => new ImageAssetOrUri({ existingImageUri: url })
    )
  }
}
